import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';

import './globals.css';
import { ThemeProvider } from './contexts/ThemeContext';
import { TenantProvider } from './contexts/TenantContext';
import { resolveTenantId } from './config/tenants';
import { NotFound } from './components/NotFound';
import { redirectRetiredChatPath } from './lib/retiredChatPaths';
import { routeTree } from './routeTree.gen';

// /all, /neuro and /derma now open the root chat, which covers every specialty,
// and old QR codes with ?location=N or ?flyer=N get ?source=location_N. Rewrite
// the address first, so the router, the tenant and source tracking all see it.
redirectRetiredChatPath();
const tenantId = resolveTenantId();

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // Unknown routes (incl. an unknown tenant segment like /unknown) render a 404
  // that links to the site root.
  defaultNotFoundComponent: NotFound,
});

// Create a QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60, // 1 hour
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function App() {
  return (
    <StrictMode>
      <TenantProvider tenantId={tenantId}>
        <ThemeProvider>
          <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
          </QueryClientProvider>
        </ThemeProvider>
      </TenantProvider>
    </StrictMode>
  );
}

const rootElement = document.getElementById('app')!;

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(<App />);
}
